import React, {forwardRef, useContext, useEffect, useImperativeHandle, useState} from "react";
import Alert from "@common/Alert";
import {Clazz, ERRORS, REMOVE_TYPES, STEPS, User} from "./BulkClassUpdate";
import Button from "@common/Button";
import axios from "@common/axios";
import {TemplateContext} from "@common/context/TemplateContext";

export interface ReviewAndSubmitRef {
    checkNextStep: () => void;
}
interface Props {
    users: Record<number, User>,
    newClasses: Record<string, Clazz>,
    schoolClasses: Record<number, object>,
    updateClasses: (loading: boolean) => void,
    completeUpdate: () => void
}
interface ClassData {
    className: string,
    userIds: number[],
    teacherIds: number[],
    studentIds: number[]
}
interface UsersData {
    totalUserUpdated: number,
    totalUserNotFoundErrors: number,
    totalUserNoClassesErrors: number,
    totalErrors: number
}

const ReviewAndSubmit = forwardRef<ReviewAndSubmitRef, Props>((props, ref) => {
    const {users, newClasses, schoolClasses, validUserCount, invalidUserCount, updateClasses, completeUpdate} = props;
    const [classesData, setClassesData] = useState<Record<string, ClassData>>({});
    const [usersData, setUsersData] = useState<UsersData>({totalUserUpdated: 0, totalUserNotFoundErrors: 0, totalUserNoClassesErrors: 0, totalErrors: 0});
    const {showSnackbar} = useContext(TemplateContext);

    const checkNextStep = (): void => {
        updateClasses(true);
        let formData = [];
        for (const [key, classData] of Object.entries(classesData)) {
            let classBean = {...classData};
            formData.push(classBean);
        }
        axios.post('/web/ui/sc/bulkClassUpdate', formData,{headers: {'Content-Type': 'application/json'}})
            .then(response => {
                completeUpdate();
            }).catch(error => {
                updateClasses(false);
            });
    }

    useEffect(() => {
        let newClassesData: Record<string, ClassData> = {};
        let newUsersData: UsersData = {totalUserUpdated: 0, totalUserNotFoundErrors: 0, totalUserNoClassesErrors: 0, totalErrors: 0};
        for (let [key, user] of Object.entries(users)) {
            if (user.removed !== REMOVE_TYPES.REMOVED) {
                if (user.error === ERRORS.USER_NOT_FOUND) {
                    newUsersData.totalUserNotFoundErrors++;
                } else if (user.error === ERRORS.NO_CLASSES) {
                    newUsersData.totalUserNoClassesErrors++;
                } else {
                    for (let userClassKey of user.classes) {
                        const matchedClass = schoolClasses[parseInt(userClassKey)];
                        let className = userClassKey;
                        if (matchedClass != null) {
                            className = matchedClass.className;
                        }

                        if (newClassesData[className] == null) newClassesData[className] = {
                            className: className,
                            userIds: [],
                            teacherIds: [],
                            studentIds: []
                        };
                        newClassesData[className].userIds.push(user.userId);
                        if (user.subscriberType === "STUDENT") {
                            newClassesData[className].studentIds.push(user.userId);
                        } else {
                            newClassesData[className].teacherIds.push(user.userId);
                        }
                    }
                    if(user.userId > 0) {
                        newUsersData.totalUserUpdated++;
                    }
                }
            }
        }
        newUsersData.totalErrors = newUsersData.totalUserNotFoundErrors + newUsersData.totalUserNoClassesErrors;
        setClassesData(newClassesData);
        setUsersData(newUsersData);
    }, [users]);

    useImperativeHandle(ref, () => ({
        checkNextStep
    }))

    const handleDownloadErrorList = (): void => {
        let csvContent: string = 'Email,Class,Error\n';
        for (let [key, user] of Object.entries(users) as [string, User][]) {
            if (user.removed !== REMOVE_TYPES.REMOVED && user.error !== ERRORS.NULL) {
                const classes = Array.isArray(user.classes) ? user.classes.join('|') : user.classes;
                csvContent += `${user.username},"${classes}","${user.error.description}"\n`;
            }
        }
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

        // Create a temporary link to trigger the download
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.setAttribute("download", "class_update_errors.csv");

        // Append the link, trigger the download, and clean up
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    return (
        <>
            <h1>Review and Submit</h1>
            <p>Review your class import before submitting.</p>
            <div className="flex largeGap margin-t--l">
                <div className="flexMax flexColumn ch-emph-block padding--l">
                    <h2>Users</h2>
                    <hr />
                    <table>
                        <tbody>
                            <tr key={0} className="margin-t--m">
                                <td>Users with updated classes:</td>
                                <td className="align-right"><strong>{usersData.totalUserUpdated}</strong></td>
                            </tr>
                            <tr key={1} className="margin-t--m">
                                <td>Users with no class:</td>
                                <td className="align-right"><strong>{usersData.totalUserNoClassesErrors}</strong></td>
                            </tr>
                            <tr key={2} className="margin-t--m">
                                <td>Users who can't be found:</td>
                                <td className="align-right"><strong>{usersData.totalUserNotFoundErrors}</strong></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className="flexMax flexColumn ch-emph-block padding--l">
                    <h2>Classes</h2>
                    <hr />
                    <table>
                        <tbody>
                            {Object.entries(classesData).sort(([a], [b]) => a.localeCompare(b)).map(([key, classData]) => (
                                <tr key={key} className="margin-t--m">
                                    <td>{key}:</td>
                                    <td className="align-right"><strong>{`${classData.studentIds.length} student${classData.studentIds.length != 1 ? 's' : ''}`}</strong>,&nbsp;<strong>{`${classData.teacherIds.length} teacher${classData.teacherIds.length != 1 ? 's' : ''}`}</strong></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {usersData.totalErrors > 0 && (
                <Alert
                    className="margin-t--l"
                    type="warning"
                    showIcon
                    description={
                        <>
                            {validUserCount == 0 &&
                                <p><strong>No users will be processed.</strong></p>
                            }
                            <p><strong>{usersData.totalErrors}</strong> {usersData.totalErrors > 0 ? "rows" : "row"} will not be processed.</p>
                            <p>Return to the {STEPS.MANAGE_USERS.title} step to review and ensure users are assigned classes.</p>
                            <Button type="outline" className="margin-t--m" onClick={handleDownloadErrorList}>Download Error List</Button>
                        </>
                    }
                />
            )}
        </>
    )
});

export default ReviewAndSubmit;